import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SiteLayout, useDocumentTitle } from '../../components/platform/SiteLayout';
import { Container, Heading, Card, Btn, Field, inputCls } from '../../components/platform/ui';
import useToolTask from '../../components/tools/useToolTask';
import { FilePicker, TaskProgress, ResultImage, DownloadButton, Slider, buildForm } from '../../components/tools/ToolShared';

const CephToolPage = () => {
  const { t } = useTranslation();
  useDocumentTitle(t('pl.toolsPage.ceph.title'));
  const task = useToolTask('tools.task.ceph');
  const [files, setFiles] = useState([]);
  const [mode, setMode] = useState('xray');
  const [slab, setSlab] = useState(1);
  const [facing, setFacing] = useState('right');
  const [rot, setRot] = useState({ x: 0, y: 0, z: 0 });

  const params = () => ({ mode, slab, facing, rotate_x: rot.x, rotate_y: rot.y, rotate_z: rot.z });
  const busy = task.phase === 'uploading' || task.phase === 'processing';
  const build = () => task.start('ceph', buildForm({ files }, params()));
  const rerun = () => task.rerun(params());
  const report = task.results?.report;

  return (
    <SiteLayout>
      <Container className="py-10 max-w-4xl">
        <Heading level={1} className="text-3xl mb-2">{t('pl.toolsPage.ceph.title')}</Heading>
        <p className="mb-6 text-gray-700">{t('pl.toolsPage.ceph.subtitle')}</p>

        <TaskProgress task={task} />

        <Card className="mb-6">
          <FilePicker files={files} onChange={setFiles} disabled={busy} />
          <Heading level={3} className="text-lg mb-3">{t('pl.toolsPage.ceph.settings')}</Heading>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t('pl.toolsPage.ceph.mode')}>
              <select className={inputCls} value={mode} disabled={busy} onChange={(e) => setMode(e.target.value)}>
                <option value="xray">{t('pl.toolsPage.ceph.modeXray')}</option>
                <option value="mip">{t('pl.toolsPage.ceph.modeMip')}</option>
              </select>
            </Field>
            <Field label={t('pl.toolsPage.ceph.facing')}>
              <select className={inputCls} value={facing} disabled={busy} onChange={(e) => setFacing(e.target.value)}>
                <option value="right">{t('pl.toolsPage.ceph.facingRight')}</option>
                <option value="left">{t('pl.toolsPage.ceph.facingLeft')}</option>
              </select>
            </Field>
          </div>
          <Slider label={t('pl.toolsPage.ceph.slab')} value={slab} min={0.2} max={1} step={0.05} onChange={setSlab} disabled={busy} />
          <div className="text-sm font-bold mt-2">{t('pl.toolsPage.ceph.rotation')}</div>
          <div className="grid sm:grid-cols-3 gap-x-4">
            {['x', 'y', 'z'].map((axis) => (
              <Slider key={axis} label={t(`pl.toolsPage.ceph.rotate${axis.toUpperCase()}`)} value={rot[axis]} min={-30} max={30} step={0.5}
                      unit="°" disabled={busy} onChange={(v) => setRot((r) => ({ ...r, [axis]: v }))} />
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-4">
            <Btn disabled={busy || files.length === 0} onClick={build}>{t('pl.toolsPage.ceph.build')}</Btn>
          </div>
          <p className="text-xs text-gray-500 mt-3">{t('pl.toolsPage.ceph.limits')}</p>
        </Card>

        {task.phase === 'done' && (
          <Card className="mb-6">
            <Heading level={3} className="text-lg mb-3">{t('pl.toolsPage.ceph.result')}</Heading>
            <ResultImage taskId={task.taskId} name="ceph.png" alt={t('pl.toolsPage.ceph.result')} className="max-w-full border-2 border-black bg-black" />
            {report && (
              <p className="text-sm mt-3 text-gray-700">
                {t('pl.toolsPage.ceph.pixel', { mm: report.pixel_size_mm })} · {t('pl.toolsPage.ceph.size', { w: report.width, h: report.height })}
              </p>
            )}
            <div className="flex flex-wrap gap-3 mt-4">
              <DownloadButton taskId={task.taskId} name="ceph.png" label={t('pl.toolsPage.ceph.downloadPng')} variant="primary" />
              <Btn variant="secondary" onClick={rerun}>{t('pl.toolsPage.common.recalc')}</Btn>
            </div>
            <p className="text-xs text-gray-500 mt-2">{t('pl.toolsPage.common.recalcHint')}</p>
            <p className="text-xs text-gray-500 mt-2">{t('pl.toolsPage.common.retention')}</p>
          </Card>
        )}
        <p className="text-sm text-gray-600">{t('pl.toolsPage.common.disclaimer')}</p>
      </Container>
    </SiteLayout>
  );
};

export default CephToolPage;
